import React, { useContext, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { get, isEmpty } from "lodash";
import {
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TablePagination,
  TableSortLabel,
  Paper,
} from "@material-ui/core";
import { ContentFinderContext } from "../../context/ContentFinderContext";
import LoadingSpinner from "../common/LoadingSpinner";
import { PageErrorContent } from "../page-error/PageError";
import ContentFinderTitleBar from "./ContentFinderTitleBar";
import { CONTENT_FINDER, NO_EPISODES_ERROR } from "../../constants/ContentFinder";
import { PAGE_ERROR } from "../../constants/MediaSearch";
import "../../styles/contentFinder.scss";
import "../dashboard/table.scss";

const episodeHeadCells = [
  { id: "TitleId", label: "Title ID", width: "16%" },
  { id: "TitleName", label: "Title Name", isLink: true, width: "42%", path: "/legacyGmsc/feature/" },
  { id: "ReleaseYear", label: "Release Year", width: "16%" },
  { id: "SeasonNumber", label: "Season Number", sortable: true, width: "13%" },
  {
    id: "SeasonEpisodeNumber",
    label: "Episode Number",
    sortable: true,
    width: "13%",
    fallbackId: "SeriesItemNumber",
  },
];

const rowsPerPageOptions = [10, 25, 50, 100];

const getCellValue = (item, cell) => {
  const primary = get(item, cell.id);
  if (primary) return primary;
  if (cell.fallbackId) {
    const fallback = get(item, cell.fallbackId);
    return fallback || '-';
  }
  return "-";
};

const compareValues = (aVal, bVal) => {
  const aNum = Number(aVal);
  const bNum = Number(bVal);
  const bothNumeric =
    aVal !== "" &&
    bVal !== "" &&
    aVal !== null &&
    bVal !== null &&
    !Number.isNaN(aNum) &&
    !Number.isNaN(bNum);
  if (bothNumeric) {
    return aNum - bNum;
  }
  return String(aVal ?? "").localeCompare(String(bVal ?? ""));
};

const getComparator = (order, orderBy, cell?) => {
  if (!orderBy || !order) return () => 0;
  const valueOf = cell
    ? (row) => getCellValue(row, cell)
    : (row) => get(row, orderBy);
  return (a, b) => {
    const result = compareValues(valueOf(a), valueOf(b));
    return order === "asc" ? result : -result;
  };
};

const stableSort = (array, comparator) => {
  const stabilized = array.map((el, index) => [el, index]);
  stabilized.sort((a, b) => {
    const ord = comparator(a[0], b[0]);
    if (ord !== 0) return ord;
    return a[1] - b[1];
  });
  return stabilized.map((el) => el[0]);
};

const ContentFinderSearchDetails = () => {
  const { titleId } = useParams<{ titleId: string }>();
  const {
    titleDetails,
    titleDetailsLoading,
    titleDetailsError,
    fetchTitleDetails,
  } = useContext(ContentFinderContext);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [order, setOrder] = useState(null);
  const [orderBy, setOrderBy] = useState(null);

  useEffect(() => {
    if (!titleId) return;
    const loadedTitleId = String(
      get(titleDetails, "[0].TitleId", "")
    );
    if (!titleDetailsLoading && loadedTitleId !== String(titleId)) {
      fetchTitleDetails(titleId, "series");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [titleId]);

  const handleChangePage = (_event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSortRequest = (cellId) => {
    const isAsc = orderBy === cellId && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(cellId);
    setPage(0);
  };

  if (titleDetailsLoading) {
    return <LoadingSpinner show={true} />;
  }

  if (titleDetailsError) {
    return (
      <div className="series-details-page">
        <div className="hbo-promo">
          <div className="hbo-promo-error">
            <PageErrorContent
              title={PAGE_ERROR.title}
              message={PAGE_ERROR.message}
              reloadBtn={false}
            />
          </div>
        </div>
      </div>
    );
  }

  const titleDetailsObj = titleDetails?.length > 0 ? titleDetails[0] : {};
  const seriesItems = get(titleDetailsObj, "SeriesItems", []);

  const orderByCell = episodeHeadCells.find((c) => c.id === orderBy);
  const sortedSeriesItems = orderBy
    ? stableSort(seriesItems, getComparator(order, orderBy, orderByCell))
    : seriesItems;

  const paginatedSeriesItems = sortedSeriesItems.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  return (
    <div className="series-details-page">
      {!isEmpty(seriesItems) && (
        <ContentFinderTitleBar titleDetails={titleDetailsObj} />
      )}

      <div
        className={`contentFinderSeriesGrid${
          seriesItems?.length > 10 ? "" : " contentFinderSeriesGrid--no-pagination"
        }`}
      >
        {isEmpty(seriesItems) ? (
          <div className="hbo-promo">
            <div className="hbo-promo-error">
              <PageErrorContent
                title={NO_EPISODES_ERROR.title}
                message={NO_EPISODES_ERROR.message}
                reloadBtn={false}
              />
            </div>
          </div>
        ) : (
          <>
            <div className="contentFinderSeriesTableInfoContainer">
              <span className="contentFinderSeriesTableInfo">
                {CONTENT_FINDER.episodeDetailsPage}
              </span>
            </div>
            <TableContainer
              component={Paper}
              className="content-finder-table-container"
            >
              <Table
                aria-label="series episodes table"
                className="content-finder-table"
              >
                <TableHead className="tableHeader content-finder-table-head">
                  <TableRow className="tableRow content-finder-header-row">
                    {episodeHeadCells.map((column, index) => (
                      <TableCell
                        key={`header_${index}`}
                        className="media-cell"
                        style={{ width: column.width }}
                        sortDirection={
                          column.sortable && orderBy === column.id
                            ? order
                            : false
                        }
                      >
                        {column.sortable ? (
                          <TableSortLabel
                            className="tableSort"
                            active={orderBy === column.id}
                            direction={
                              orderBy === column.id ? order || "asc" : "asc"
                            }
                            onClick={() => handleSortRequest(column.id)}
                          >
                            {column.label}
                          </TableSortLabel>
                        ) : (
                          column.label
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>

                <TableBody id="table_container" className="content-finder-table-body">
                  {paginatedSeriesItems.map((item, index) => (
                    <TableRow
                      className="tableRow content-finder-body-row"
                      key={item.TitleId || index}
                    >
                      {episodeHeadCells.map((cell) => (
                        <TableCell
                          key={cell.id}
                          className="content-finder-table-cell"
                          style={{ width: cell.width }}
                        >
                          {cell.isLink ? (
                            <Link
                              to={`${cell.path}${item.TitleId}`}
                              onClick={(e) => {
                                fetchTitleDetails(item.TitleId, "feature");
                              }}
                              className="contentFinderTableCellLink"
                            >
                              {getCellValue(item, cell)}
                            </Link>
                          ) : (
                            getCellValue(item, cell)
                          )}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}
      </div>

      {seriesItems?.length > 10 && (
        <TablePagination
          component="div"
          className="tablePagination"
          classes={{
            toolbar: "tablePaginationToolbar",
          }}
          count={seriesItems.length}
          page={page}
          rowsPerPage={rowsPerPage}
          rowsPerPageOptions={rowsPerPageOptions}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          nextIconButtonText="Next Page"
          backIconButtonText="Previous Page"
        />
      )}
    </div>
  );
};

export default ContentFinderSearchDetails;
